import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { client, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, Lock, AlertTriangle } from "lucide-react";

/**
 * One-time admin bootstrap page.
 * Visible ONLY when the backend reports availability=true. The bootstrap
 * secret must be shared with the operator via a private channel (never a URL
 * parameter, never in the page HTML, never pre-filled). The user pastes the
 * secret and chooses a password. On success the user is redirected to login.
 *
 * The page intentionally does not distinguish between "endpoint disabled",
 * "wrong secret" and "already consumed" — all surface as the same generic
 * message so an attacker cannot enumerate preconditions.
 */
const SetupPage = () => {
  const nav = useNavigate();
  const [checking, setChecking] = useState(true);
  const [available, setAvailable] = useState(false);
  const [secret, setSecret] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await client.get("/auth/bootstrap-admin/status");
        setAvailable(!!r.data?.available);
      } catch {
        setAvailable(false);
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  const digitsOnly = (s) => (s || "").replace(/\D/g, "").slice(0, 4);
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw !== pw2) { setErr("PIN-koderna matchar inte."); return; }
    if (!/^\d{4}$/.test(pw)) { setErr("PIN-koden måste vara exakt fyra siffror."); return; }
    if (!secret || secret.length < 32) { setErr("Hemligheten ser inte giltig ut."); return; }
    setSubmitting(true);
    try {
      await client.post("/auth/bootstrap-admin", { secret, newPin: pw });
      toast.success("Admin aktiverad. Logga in med din nya PIN-kod.");
      // Clear in-memory copies
      setSecret(""); setPw(""); setPw2("");
      nav("/login", { replace: true });
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">
        Kontrollerar…
      </div>
    );
  }

  if (!available) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div data-testid="setup-unavailable"
             className="max-w-md bg-white rounded-2xl shadow-sm p-8 text-center">
          <AlertTriangle className="h-10 w-10 text-slate-400 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-slate-800 mb-2"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            Setup är inte tillgänglig
          </h1>
          <p className="text-sm text-slate-500">
            Engångsaktiveringen är avstängd, utgången eller redan använd.
            Kontakta ägaren om du behöver åtkomst.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6"
         style={{ background: "linear-gradient(135deg,#F8FBFF 0%,#EDF4FA 100%)" }}>
      <form onSubmit={submit}
            data-testid="setup-form"
            className="w-full max-w-md bg-white rounded-2xl shadow-sm p-8 space-y-5">
        <div className="text-center">
          <ShieldCheck className="h-10 w-10 mx-auto mb-2" style={{ color: "#3B9EDA" }} />
          <h1 className="text-2xl font-bold text-slate-800"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            Första adminaktivering
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Engångsflöde. Hemligheten delades med dig privat av ägaren.
            Välj därefter en PIN-kod på fyra siffror.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="s">Bootstrap-hemlighet</Label>
          <Input id="s" type="password"
                 data-testid="setup-secret-input"
                 autoComplete="off"
                 value={secret} onChange={(e) => setSecret(e.target.value)} />
          <p className="text-[11px] text-slate-400">
            Klistra in exakt. Hemligheten skrivs aldrig till logg eller URL.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="pw">Välj PIN-kod (exakt 4 siffror)</Label>
          <Input id="pw" type="password"
                 inputMode="numeric"
                 pattern="[0-9]{4}"
                 maxLength={4}
                 data-testid="setup-pw-input"
                 autoComplete="new-password"
                 value={pw}
                 onChange={(e) => setPw(digitsOnly(e.target.value))} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="pw2">Bekräfta PIN-kod</Label>
          <Input id="pw2" type="password"
                 inputMode="numeric"
                 pattern="[0-9]{4}"
                 maxLength={4}
                 data-testid="setup-pw2-input"
                 autoComplete="new-password"
                 value={pw2}
                 onChange={(e) => setPw2(digitsOnly(e.target.value))} />
        </div>

        {err && (
          <p data-testid="setup-error"
             className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">
            {err}
          </p>
        )}

        <Button type="submit"
                data-testid="setup-submit"
                disabled={submitting}
                className="w-full rounded-full text-white"
                style={{ backgroundColor: "#3B9EDA" }}>
          <Lock className="h-4 w-4 mr-2" />
          {submitting ? "Aktiverar…" : "Aktivera adminkonto"}
        </Button>
      </form>
    </div>
  );
};

export default SetupPage;
